import { useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { railStore } from '@/stores/RailStore';
import { tabsStore } from '@/stores/TabsStore';
import { entityStore } from '@/stores/EntityStore';
import { buildBreadcrumbs } from '@/features/layout/logic';

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function PanelRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  );
}

function PageWidthIcon({ centered, className }: { centered: boolean; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      {centered ? <path d="M8 4v16M16 4v16" /> : <path d="M6 8h12M6 12h12M6 16h12" />}
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

interface NavbarProps {
  onMobileMenuOpen: () => void;
  /**
   * Receives the element the open page portals its controls into — its active
   * filters, its settings menu and its primary action. See
   * features/tabs/PaneChrome.tsx for who is allowed to claim it.
   */
  onControlsSlot?: (el: HTMLElement | null) => void;
}

export const Navbar = observer(({ onMobileMenuOpen, onControlsSlot }: NavbarProps) => {
  const { t } = useI18n();
  const location = useLocation();
  const breadcrumbs = buildBreadcrumbs(location.pathname, t, entityStore.currentEntity);
  const focusedGroup = tabsStore.focusedGroup;
  const contentCentered = focusedGroup?.width === 'contained' && focusedGroup.align === 'center';

  return (
    <header className="sticky top-0 z-30 flex items-center gap-2 h-12 px-4 bg-background/95 backdrop-blur border-b border-border">
      {/* Mobile hamburger */}
      <button
        onClick={onMobileMenuOpen}
        className="md:hidden mr-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label={t('layout.navbar.openMenu')}
      >
        <MenuIcon className="h-5 w-5" />
      </button>

      {/* Breadcrumb. It gives way before the page's own controls do: a crumb
          trail is where you came from, and the controls are what you are doing. */}
      <nav aria-label={t('layout.navbar.breadcrumb')} className="flex items-center gap-1 text-sm min-w-0 shrink">
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.href} className="flex items-center gap-1 min-w-0">
            {i > 0 && <ChevronRightIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
            {i === breadcrumbs.length - 1 ? (
              <span className="font-medium text-foreground truncate">{crumb.label}</span>
            ) : (
              <a href={crumb.href} className="text-muted-foreground hover:text-foreground truncate transition-colors">
                {crumb.label}
              </a>
            )}
          </span>
        ))}
      </nav>

      {/* What the open page hangs here: its active filters, one settings menu
          and its primary action. Empty on pages that publish nothing, which is
          why the row collapses to the breadcrumb and the rail toggle. */}
      <div
        ref={onControlsSlot}
        className="no-scrollbar flex min-w-0 items-center gap-1.5 overflow-x-auto ml-auto"
      />

      {/* Right actions */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => {
            if (!focusedGroup) return;
            tabsStore.setPresentation(focusedGroup.id, contentCentered
              ? { width: 'full', align: 'center' }
              : { width: 'contained', align: 'center' });
          }}
          disabled={!focusedGroup}
          className={`p-1.5 rounded-md transition-colors ${
            contentCentered
              ? 'bg-accent text-accent-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
          aria-label={t(contentCentered ? 'layout.navbar.useFullWidth' : 'layout.navbar.centerContent')}
          title={t(contentCentered ? 'layout.navbar.useFullWidth' : 'layout.navbar.centerContent')}
          aria-pressed={contentCentered}
        >
          <PageWidthIcon centered={contentCentered} className="h-4 w-4" />
        </button>
        {/* Rail toggle — always here, even when the panel has nothing to show.
            A control that comes and goes with the page is one people stop
            reaching for; the panel says "nothing here" itself instead. */}
        <button
          onClick={() => railStore.toggle()}
          className={`p-1.5 rounded-md transition-colors ${
            railStore.isOpen
              ? 'bg-accent text-accent-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
          aria-label={t('layout.navbar.toggleRail')}
          aria-pressed={railStore.isOpen}
        >
          <PanelRightIcon className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
});
